import React, { useEffect, useRef } from 'react';
import Button from '@cvent/carina/components/Button';
import Modal from '@cvent/carina/components/Modal';
import Col from '@cvent/carina/components/Col';
import Row from '@cvent/carina/components/Row';
import useBlockTheme from '@cvent/blocks/hooks/useTheme';
import { CSSObject, keyframes } from '@emotion/react';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import { useTranslate } from 'nucleus-text';
import TextLink from '@cvent/carina/components/TextLink';
import { confirmationCheckLottie } from './confirmationCheckLottie';

interface RegistrationConfirmationModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  firstName: string;
  lastName: string;
  loginUrl: string;
  hideLoginSection?: boolean;
}

const useStyles = (): Record<string, CSSObject> => {
  const { font, block } = useBlockTheme();
  const fadeIn = keyframes`
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  `;
  return {
    modalContainer: {
      padding: '2rem',
      backgroundColor: block.container
    },
    mainText: {
      fontSize: font.base.size[5],
      paddingBottom: '0.5rem'
    },
    okButton: {
      marginTop: '1rem'
    },
    spotReservedMessage: {
      paddingLeft: '0.25rem',
      flexGrow: 1,
      fontSize: font.base.size[5],
      width: 'calc(100% - 48px)',
      fontWeight: font.base.weight.bold,
      textAlign: 'left',
      opacity: 0,
      animation: `${fadeIn} 1s ease-in 0.75s`,
      animationFillMode: 'forwards',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      overflowWrap: 'break-word'
    },
    resendMessage: {
      paddingRight: '0.25rem'
    }
  };
};

function RegistrationConfirmationModal({
  isOpen,
  onDismiss,
  firstName,
  lastName,
  loginUrl,
  hideLoginSection = false
}: RegistrationConfirmationModalProps): JSX.Element {
  const { translate } = useTranslate();
  const { mainText, modalContainer, okButton, spotReservedMessage, resendMessage } = useStyles();
  const lottieRef = useRef<LottieRefCurrentProps>();
  const {
    font: {
      color: { base: baseTextColor }
    }
  } = useBlockTheme();
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (lottieRef.current) {
          lottieRef.current.play();
        }
      }, 250);
    }
  }, [lottieRef, isOpen]);
  return (
    <Modal isOpen={isOpen} format="m">
      <div css={modalContainer}>
        <Col padding={0}>
          <Row margin={{ bottom: 16 }}>
            <Lottie
              animationData={confirmationCheckLottie(baseTextColor)}
              autoplay={false}
              lottieRef={lottieRef}
              loop={false}
              style={{ height: '48px' }}
            />
            <div css={spotReservedMessage}>
              {translate('LandingPage-ConfirmationModal-SpotReserved', {
                firstName,
                lastName
              })}
            </div>
          </Row>
          <Row>
            <div css={mainText}>
              {translate(
                hideLoginSection ? 'LandingPage-ConfirmationModal-Text' : 'LandingPage-ConfirmationModal-Message'
              )}
            </div>
            {!hideLoginSection && (
              <>
                <span css={resendMessage}>{translate('Landing-ConfirmationModal-ResendEmailMessage')}</span>
                <TextLink href={loginUrl}>{translate('Landing-ConfirmationModal-ResendEmailLink')}</TextLink>
              </>
            )}
          </Row>
          <Row>
            <Button css={okButton} text={translate('LandingPage-ConfirmationModal-Ok')} onClick={onDismiss} />
          </Row>
        </Col>
      </div>
    </Modal>
  );
}

export default RegistrationConfirmationModal;
